import React from 'react';
import classnames from 'classnames';
import { getTextColorBasedOnBG } from 'src/designTokens/themes/utils';
import styles from './index.module.scss';

type AvatarProps = {
  imageUrl?: string;
  text?: string;
  shape?: 'circle' | 'square' | 'rounded';
  size?: 'small' | 'medium' | 'large';
  background?: string;
  'data-test-id'?: string;
  outlined?: boolean;
  classNames?: string;
};

function Avatar({
  imageUrl,
  text,
  shape = 'circle',
  size = 'medium',
  background = '#f8e7ea',
  'data-test-id': dataTestId = 'avatar',
  outlined = false,
  classNames = '',
  children,
}: React.PropsWithChildren<AvatarProps>) {
  const avatarClassnames = classnames(
    styles.avatar,
    classNames,
    styles[`avatar__${shape}`],
    styles[`avatar__${size}`],
    {
      [styles.avatar__outlined]: outlined,
    },
  );

  const textClassnames = classnames(
    styles.avatar__text,
    styles[`avatar__text__${size}`],
  );
  return (
    <div style={{ backgroundColor: background }} className={avatarClassnames}>
      {imageUrl && (
        <img
          src={imageUrl}
          data-test-id={`${dataTestId}-image`}
          alt={text}
          className={styles.avatar__image}
        />
      )}
      {text && !imageUrl && (
        <span
          data-test-id={`${dataTestId}-text`}
          className={textClassnames}
          style={{ color: getTextColorBasedOnBG(background) }}
        >
          {text}
        </span>
      )}
      {children}
    </div>
  );
}

type AvatarGroupProps = {
  items: Array<AvatarProps>;
  size?: 'small' | 'medium' | 'large';
};

export function AvatarGroup({ items = [], size = 'medium' }: AvatarGroupProps) {
  const getAvatarStyles = (index) =>
    classnames({
      [styles[`avatarGroup__item__${size}`]]: index > 0,
    });
  return (
    <div className={styles.avatarGroup}>
      {items.slice(0, 3).map((item, index) => (
        <Avatar
          {...item}
          outlined
          size={size}
          classNames={getAvatarStyles(index)}
        />
      ))}
      {items.length > 3 && (
        <Avatar
          text={`+${items.length - 3}`}
          size={size}
          background="var(--brand-primary)"
          outlined
          data-test-id="avatar-group-more"
          classNames={getAvatarStyles(3)}
        />
      )}
    </div>
  );
}

export default Avatar;
